import moment from "moment";

import { PredefinedAppointment } from "@/Appointments/Api/Dto/Responses/PredefinedAppointment";
import { PredefinedRuleSet, PredefinedRuleSetItem } from "@/Appointments/Api/Dto/Responses/PredefinedRuleSet";
import { PredefinedAppointmentGroup } from "@/Appointments/Models/Scheduling/PredefinedAppointmentGroup";
import { PredefinedAppointmentItem } from "@/Appointments/Models/Scheduling/PredefinedAppointmentItem";
import { CalendarSlotV2 } from "@/Appointments/Models/Scheduling/WorkDayV2";
import { Appointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { ServiceComboTargetClientele, ToTargetedClienteleTranslatedValue } from "@/Common/Models/Services/Enums/ServiceComboTargetClientele";
import { ServiceDefinitionModel } from "@/Common/Models/Services/ServiceDefinitionModel";
import { CalendarSlotType } from "@/Common/Models/UI/Enums";

import styles from "./CalendarSlotCell.module.scss";

import img_icon_pictogrammemyle_emploisdutemps_assigne from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Assigne.png";
import img_icon_pictogrammemyle_emploisdutemps_clinique from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Clinique.png";
import img_icon_pictogrammemyle_emploisdutemps_organization from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Organization.png";
import img_icon_pictogrammemyle_emploisdutemps_ouvert from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Ouvert.png";
import img_icon_pictogrammemyle_emploisdutemps_rdvonline from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-RDVOnline.png";
import img_icon_pictogrammemyle_emploisdutemps_rdvurgent from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-RDVUrgent.png";
import img_icon_pictogrammemyle_emploisdutemps_site from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Site.png";
import img_icon_pictogrammemyle_emploisdutemps_teleconsultation from "@/Globals/_images/icons/admin/PictogrammeMYLE-EmploisDuTemps-Teleconsultation.png";

interface ICalendarSlotCellProps {
    slot: CalendarSlotV2;
    index: number;
    hasSideBar: boolean;
    fullWidth: boolean;
    selectedRulesetId: number;
    minorAxisResolutionInMinutes: number;
    showObjectDeleteX?: boolean;
    onClick?: (slotModel: any) => void;
    onDeleteSlotClick?: (slot: CalendarSlotV2) => void;
    onRulesetClick: (slot: CalendarSlotV2) => void;
}

class CalendarSlotCell extends React.Component<ICalendarSlotCellProps, null> {
    private fromText: string;
    private toText: string;
    private cellHeightDefault = 1.6; // em

    private readonly predefinedAppointmentGroupBackgroundStyle = "repeating-linear-gradient( 45deg, #0070d2, #0050d2, 10px, #0070d2 10px, #0070d2 20px )";
    private readonly predefinedGroupAnySlotFromRuleSetBackgroundStyle = "repeating-linear-gradient( 0deg, #0070d2, #0050d2, 10px, #0070d2 10px, #0070d2 20px )";

    constructor(props: ICalendarSlotCellProps) {
        super(props);
        this.fromText = cacheStore.GetString("FromLabel");
        this.toText = cacheStore.GetString("ToLabel");
    }

    public render(): JSX.Element {
        const { slot, index } = this.props;

        const innerHeight = this.cellHeightEm(slot.DisplayedDurationInMinutesComputed);
        const marginTopOffsetEm = Math.round(this.cellHeightDefault * slot.minorAxisPositionIndexFractionPourcent) / 100;
        const totalWidth = this.props.fullWidth == true ? 100 : 80;
        const widthPercent = (totalWidth / slot.MaxCollisionsInCollisionRange);
        const positionOffset = (slot.HorizontalPositionIndexZeroBase * widthPercent);
        const groupedModel: PredefinedAppointmentGroup = slot.Model;
        let style: React.CSSProperties = { height: `${innerHeight}em`, width: "100%" };

        if (groupedModel && groupedModel.IsOptimizedSearch) {
            style.background = this.predefinedAppointmentGroupBackgroundStyle;
        }
        else if (slot.Type == CalendarSlotType.PredefinedGroup && groupedModel.PredefinedAppointments) {
            let areAnySlotsFromRuleSet = false;
            Object.keys(groupedModel.PredefinedAppointments).forEach((key: string) => {
                const slots = groupedModel.PredefinedAppointments[key];
                if (slots)
                    areAnySlotsFromRuleSet = slots.some(x => x.IsFromRuleSetAvailability == true);
            });
            if (areAnySlotsFromRuleSet)
                style.background = this.predefinedGroupAnySlotFromRuleSetBackgroundStyle;
        }

        return (
            <div
                key={`${index}_cell_${slot.id}`}
                onClick={() => this.props.onClick && this.props.onClick(slot.Model)}
                className="scheduled-eventV2"
                title={this.getSlotCellTitle(slot)}
                style={{
                    height: `${innerHeight}em`,
                    overflow: "hidden",
                    width: `${widthPercent}%`,
                    marginTop: `${marginTopOffsetEm}em`,
                    marginLeft: `${positionOffset}%`
                }}
            >
                <div className={`flx ${slot.GetColorClass()}`} style={style} key={`app_r_${index}`}>
                    {this.getSlotCellElement(slot)}
                    {this.props.showObjectDeleteX
                        && this.props.onDeleteSlotClick
                        && (slot.Type === CalendarSlotType.PredefinedAppointment || slot.Type === CalendarSlotType.RuleSet)
                        && (
                            <div className={`hiddenV2 text-right ${styles.calendarSlotCellCloseIconContainer}`}>
                                <a onClick={() => this.props.onDeleteSlotClick(slot)}>
                                    <strong>x</strong>
                                </a>
                            </div>
                        )
                    }
                </div>
            </div>

        );
    }

    private getSlotCellTitle(slot: CalendarSlotV2): string {
        if (slot.Type !== CalendarSlotType.PredefinedAppointment || !cacheStore.ClinicSettings.EnableOnlinePredefinedAppointments)
            return;

        let predefinedAppointment: Partial<Appointment>;
        if (slot.Model instanceof ServiceDefinitionModel) {
            predefinedAppointment = {
                ...slot.Model,
                IsOnlineBookingAllowed: slot.Model.Model.IsAvailableOnline,
                TargetedClient: slot.Model.Model.OnlineClienenteleCible
            };
        }
        else {
            predefinedAppointment = slot.Model;
        }

        return `${this.getFromToTimeString(slot, "-")} ${predefinedAppointment.Name}\n`
            + `${predefinedAppointment.IsOnlineBookingAllowed ? `${cacheStore.GetString("AvailableOnPortalLabel")}\n` : ""}`
            + `${cacheStore.GetString("ClienteleLabel")}: ${ToTargetedClienteleTranslatedValue(predefinedAppointment.TargetedClient)}`;
    }

    private getSlotCellElement(slot: CalendarSlotV2): JSX.Element {
        switch (slot.Type) {
            case CalendarSlotType.Appointment:
                return this.getRenderedAppointment(slot);
            case CalendarSlotType.RuleSet:
                return this.getRenderedRuleSet(slot);
            case CalendarSlotType.PredefinedAppointment:
                return this.getRenderedPredefined(slot);
            case CalendarSlotType.PredefinedGroup:
                return cacheStore.ClinicSettings.EnablePatientChartBookingAppointmentInfo
                    ? this.getRenderedPredefinedGroupWithInfo(slot)
                    : this.getRenderedPredefinedGroup(slot);
            default:
                return null;
        }
    }

    private getRenderedAppointment(slot: CalendarSlotV2): JSX.Element {
        const appt: PredefinedAppointment = slot.Model as PredefinedAppointment;
        return appt != null
            ? <span> {this.fromText} {this.getFromToTimeString(slot)} </span>
            : null;
    }

    private getRenderedPredefined(slot: CalendarSlotV2): JSX.Element {
        const appt: Appointment = slot.Model as Appointment;
        return appt != null
            ? <span style={{ width: "98%" }}> <strong> {appt.Name}: </strong> {this.getFromToTimeString(slot)} </span>
            : null;
    }

    private getRenderedRuleSet(slot: CalendarSlotV2): JSX.Element {
        const rs: PredefinedRuleSet = slot.Model as PredefinedRuleSet;
        const styleClass = `ruleset-container ${this.props.selectedRulesetId == slot.Model.ID ? "ruleset-selected" : ""}`;
        let rulesetRows: JSX.Element[] = [];

        if (rs === null)
            return null;

        if (rs.PredefinedRuleSetItems != null) {
            const distinctServices = rs.PredefinedRuleSetItems.toImmutable().groupBy(x => x.ServiceDefinitionId);
            rulesetRows = distinctServices
                .map((group: Immutable.List<PredefinedRuleSetItem>) => this.getRuleSetRows(group))
                .toArray();
        }

        return (
            <div className={styleClass} onClick={this.props.onRulesetClick.bind(this, slot)}>
                <span> {this.fromText} {this.getFromToTimeString(slot)} </span>
                <section className="ruleset-table">
                    {rulesetRows}
                </section>
            </div>
        );
    }

    private getRuleSetRows(group: Immutable.List<PredefinedRuleSetItem>): JSX.Element {
        const x = group.get(0);

        const isAvailableOnline = group.some(x => x.IsOnlineBookingAllowed);
        const isUrgent = group.some(x => x.IsUrgent);
        const isOpenClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.Open);
        const isClinicalClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.Clinic);
        const isSiteClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.Site);
        const isOrganizationClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.Organization);
        const isAssignedClientele = group.some(x => x.TargetedClient == ServiceComboTargetClientele.AssignedClientele);
        const isTeleconsultation = group.filter(x => IsNullOrUndefined(x.ComboId)).some(x => x.IsTeleconsultation);
        const hasStandalone = group.filter(z => z.ComboId == null).count() > 0;
        const standAloneMarker = hasStandalone ? "+" : "";
        const nbCombos = group.filter(z => z.ComboId != null).count();

        const images = this.getListRuleSetImages(
            x.ID,
            isAvailableOnline,
            isUrgent,
            isOpenClientele,
            isClinicalClientele,
            isSiteClientele,
            isOrganizationClientele,
            isAssignedClientele,
            isTeleconsultation
        );

        let sumMin = 0;
        group.forEach(x => {
            sumMin += x.Min;
        });
        const sumMinDisplay = sumMin == 0 ? "-" : sumMin.toString();
        let groupMax = group.toArray()[0].GroupMax;

        // if we only have one service in group and groupMax is zero, take the max from the service
        if (group.count() === 1 && groupMax === 0)
            groupMax = group.toArray()[0].Max;

        const denominatorDisplay = (groupMax == 0) ? "-" : groupMax.toString();
        const counters = sumMinDisplay + " | " + denominatorDisplay;
        let display: JSX.Element;

        if (nbCombos >= 1) {
            display = (
                <div className="flex-container left">
                    <div>
                        <span>{cacheStore.GetLocalizedValueByLookupId(x.ServiceNameLookupId)}</span>
                    </div>
                    <div className={styles.calendarSlotCellCloseImagesMarginLeft}>
                        <span>{`(${standAloneMarker}${nbCombos}${nbCombos > 1 ? " Combos" : " Combo"})`}</span>
                    </div>
                </div>
            );
        }
        else {
            display = (
                <span style={{ textAlign: "left" }}>
                    {cacheStore.GetLocalizedValueByLookupId(x.ServiceNameLookupId) + " (" + x.DurationMinutes + " " + cacheStore.GetString("MinuteLLabel") + ")"}
                </span>
            );
        }

        return (
            <div className="ruleset-row" key={x.ID}>
                <span style={{ "width": "50%" }} className="ruleset-cell noinline"> {display} </span>
                <span style={{ "width": "30%" }} className="ruleset-cell noinline">
                    {images}
                </span>
                <span style={{ "width": "20%" }} className="ruleset-cell noinline"> {counters} </span>
            </div>
        );
    }

    private getListRuleSetImages(
        id: string,
        isAvailableOnline: boolean,
        isUrgent: boolean,
        isOpenClientele: boolean,
        isClinicalClientele: boolean,
        isSiteClientele: boolean,
        isOrganizationClientele: boolean,
        isAssignedClientele: boolean,
        isTeleconsultation: boolean
    ): JSX.Element {
        const images: JSX.Element[] = [];

        if (isAvailableOnline) {
            images.push(
                <div className="img-placeholder" key={`img_online_${id}`}>
                    <img src={img_icon_pictogrammemyle_emploisdutemps_rdvonline} className={styles.calendarSlotCellCloseImagesHeight30} />
                </div>
            );
        }

        if (isUrgent) {
            images.push(
                <div className="img-placeholder" key={`img_urgent_${id}`}>
                    <img src={img_icon_pictogrammemyle_emploisdutemps_rdvurgent} className={styles.calendarSlotCellCloseImagesHeight30} />
                </div>
            );
        }

        if (isOpenClientele) {
            images.push(
                <div className="img-placeholder" key={`img_ouvert_${id}`}>
                    <img src={img_icon_pictogrammemyle_emploisdutemps_ouvert} className={styles.calendarSlotCellCloseImagesHeight20} />
                </div>
            );
        }

        if (isClinicalClientele) {
            images.push(
                <div className="img-placeholder" key={`img_clinic_${id}`}>
                    <img src={img_icon_pictogrammemyle_emploisdutemps_clinique} className={styles.calendarSlotCellCloseImagesHeight20} />
                </div>
            );
        }

        if (isSiteClientele) {
            images.push(
                <div className="img-placeholder" key={`img_clinic_${id}`}>
                    <img src={img_icon_pictogrammemyle_emploisdutemps_site} className={styles.calendarSlotCellCloseImagesHeight20} />
                </div>
            );
        }

        if (isOrganizationClientele) {
            images.push(
                <div className="img-placeholder" key={`img_clinic_${id}`}>
                    <img src={img_icon_pictogrammemyle_emploisdutemps_organization} className={styles.calendarSlotCellCloseImagesHeight20} />
                </div>
            );
        }

        if (isAssignedClientele) {
            images.push(
                <div className="img-placeholder" key={`img_assigned_${id}`}>
                    <img src={img_icon_pictogrammemyle_emploisdutemps_assigne} className={styles.calendarSlotCellCloseImagesHeight20} />
                </div>
            );
        }

        if (isTeleconsultation) {
            images.push(
                <div className="img-placeholder" key={`img_telec_${id}`}>
                    <img src={img_icon_pictogrammemyle_emploisdutemps_teleconsultation} className={styles.calendarSlotCellCloseImagesHeight30} style={{ "paddingBottom": "1px" }} />
                </div>
            );
        }

        return (
            <div className="flex-container left">
                {images}
            </div>
        );
    }

    private getRenderedPredefinedGroup(slot: CalendarSlotV2): JSX.Element {
        const appt: PredefinedAppointmentGroup = slot.Model as PredefinedAppointmentGroup;
        const keys = Object.keys(appt.PredefinedAppointments);
        const warningSign = (keys && keys.length > 1 && appt.PredefinedAppointments[keys[1]][0].GetEnd().valueOf() < moment(appt.PredefinedAppointments[keys[1]][0].Start).toDate().valueOf())
            ? "/!\\"
            : "";

        return appt != null
            ? <span>{this.getFromToTimeString(slot)} {warningSign} </span>
            : null;
    }

    private getRenderedPredefinedGroupWithInfo(slot: CalendarSlotV2): JSX.Element {
        const appt: PredefinedAppointmentGroup = slot.Model as PredefinedAppointmentGroup;

        if (!appt)
            return null;

        const keys = Object.keys(appt.PredefinedAppointments);
        const warningSign = (keys && keys.length > 1 && appt.PredefinedAppointments[keys[1]][0].GetEnd().valueOf() < moment(appt.PredefinedAppointments[keys[1]][0].Start).toDate().valueOf())
            ? "/!\\"
            : "";

        const { siteId, employeeId, serviceDefinitionId, comboId } = this.getServiceInfo(appt);

        return (
            <span>
                <strong>{this.getFromToTimeString(slot)} {warningSign}</strong> {Employee.getFullNameWithPrefixContactFacet(employeeId)}<br />
                {cacheStore.GetSiteById(siteId)?.Model.ClinicalName}: {comboId ? cacheStore.GetComboName(comboId) : cacheStore.GetServiceDefinitionName(serviceDefinitionId)}
            </span>
        );
    }

    private getServiceInfo(appt: PredefinedAppointmentGroup): {
        siteId: string,
        employeeId: string,
        serviceDefinitionId: string,
        comboId?: number
    } {
        if (appt.IsOptimizedSearch && appt.OptmizedResult) {
            return {
                siteId: appt.OptmizedResult.SiteId,
                employeeId: appt.OptmizedResult.PrimaryServiceEmployeeId,
                comboId: appt.OptmizedResult.ComboId,
                serviceDefinitionId: appt.OptmizedResult.ServiceMiddle.ServiceDefinitionId
            };
        }

        const predefinedAppointment: PredefinedAppointmentItem = Object.values(appt.PredefinedAppointments).flat().firstOrDefault();
        if (predefinedAppointment) {
            return {
                siteId: predefinedAppointment.SiteId,
                employeeId: predefinedAppointment.EmployeeId,
                serviceDefinitionId: predefinedAppointment.ServiceDefinitionId
            };
        }

        return null;
    }

    private cellHeightEm(duration?: number): number {
        if (duration == null) return this.cellHeightDefault;
        let cells = Math.round(100 * duration / this.props.minorAxisResolutionInMinutes) / 100;

        return cells * this.cellHeightDefault;
    }

    private getFromToTimeString(slot: CalendarSlotV2, splitCharacter?: string): string {
        return `${moment(slot.fromRaw).format("HH:mm")} ${splitCharacter ?? this.toText} ${moment(slot.toRAW).format("HH:mm")}`;
    }
}

export { CalendarSlotCell };
